import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import {
  Wallet,
  ShoppingCart,
  TrendingUp,
  Sparkles,
  MessageCircle,
  ArrowUpRight,
  CheckCircle2,
  Edit3,
  ArrowLeft,
  Share2,
  Download,
  ShoppingBag,
  Plus,
} from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import ShareableCard from "@/components/ShareableCard";
import UploadWarningsBanner from "@/components/UploadWarningsBanner";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import InvoiceHistoryTable from "@/components/InvoiceHistoryTable";
import PaidByPieCard from "@/components/PaidByPieCard";
import UploadReminderNudge from "@/components/UploadReminderNudge";
import SpendTimeline from "@/components/SpendTimeline";
import PlanBadge from "@/components/PlanBadge";
import FamilyMembersCard from "@/components/FamilyMembersCard";
import BudgetHistoryCard from "@/components/BudgetHistoryCard";
import UpgradeQuoteModal from "@/components/UpgradeQuoteModal";
import SubscriptionCelebrationModal from "@/components/SubscriptionCelebrationModal";
import AppShell from "@/components/AppShell";
import WeeklyListSheet from "@/components/WeeklyListSheet";
import EarlyAdopterPrompt from "@/components/EarlyAdopterPrompt";
import MonthCompare from "@/components/MonthCompare";
import CartIQAsk from "@/components/CartIQAsk";
import MyDashboardOverview from "@/components/MyDashboardOverview";
import SharedSpaceView from "@/components/SharedSpaceView";
import { useAuth } from "@/contexts/AuthContext";
import { openRazorpayCheckout } from "@/lib/razorpay";
import jsPDF from "jspdf";
import {
  fetchReport,
  updateBudget,
  connectWhatsapp,
  confirmOrder,
  pollReportUntilReady,
} from "@/lib/api";
import { formatINR, formatShortINR, percent, formatDate } from "@/lib/format";

const PLATFORM_COLORS = {
  Blinkit: "#FFCB11",
  Zepto: "#9D3CFF",
  Swiggy: "#FC8019",
  "Swiggy Instamart": "#FC8019",
  Zomato: "#E23744",
  "Uber Eats": "#06C167",
  BigBasket: "#A5CD39",
  Dunzo: "#00D3A7",
  Other: "#FF6B00",
};

const insightIcon = (type) => {
  if (type === "spend") return Wallet;
  if (type === "pattern") return TrendingUp;
  return Sparkles;
};

const DashboardTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div className="bg-[#0A0A0F] border border-[#FF6B00] p-2 text-xs font-mono-num">
      <div className="uppercase tracking-wider text-[#888] text-[10px]">{p.name}</div>
      <div className="text-[#F0EDE8]">{formatINR(p.value)}</div>
    </div>
  );
};

export default function Dashboard() {
  const { reportId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const [report, setReport] = useState(state?.reportData || null);
  const [invoiceHistory, setInvoiceHistory] = useState(state?.invoiceHistory || []);
  const [userId, setUserId] = useState(state?.userId || null);
  const [phone, setPhone] = useState(state?.phone || "");
  const [loading, setLoading] = useState(!state?.reportData);
  const [budget, setBudget] = useState(state?.reportData?.budgetStatus?.budget || 9000);
  const [editingBudget, setEditingBudget] = useState(false);
  const [budgetInput, setBudgetInput] = useState(
    String(state?.reportData?.budgetStatus?.budget || 9000)
  );
  const [waPhone, setWaPhone] = useState(state?.phone || "");
  const [waConnected, setWaConnected] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [payingPlan, setPayingPlan] = useState(null);
  // E1: upgrade-quote modal state shown before Razorpay opens for paid users
  const [quotePlan, setQuotePlan] = useState(null);
  // E2: celebration modal state
  const [celebration, setCelebration] = useState(null);
  // Tab navigation — hash-synced so deep links + back button work intuitively.
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === "undefined") return "home";
    const h = window.location.hash.replace("#", "");
    return ["home", "spending", "budget", "upload"].includes(h) ? h : "home";
  });
  const [weeklySheetOpen, setWeeklySheetOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const { user: authUser, loading: authLoading, refresh: refreshAuth } = useAuth();

  // Privacy guard: if a user signs out (or arrives without a session) and the
  // report is bound to a user_id, blow away every piece of cached state and
  // redirect to "/" so the previous user's numbers can't render under a
  // "SIGN IN" header (#feedback fix).
  useEffect(() => {
    if (authLoading) return;
    if (authUser) return;
    const ownerOnReport = report?.userId || userId;
    if (ownerOnReport) {
      setReport(null);
      setInvoiceHistory([]);
      setUserId(null);
      setBudget(9000);
      setLoading(false);
      navigate("/", { replace: true });
    }
  }, [authLoading, authUser, report?.userId, userId, navigate]);

  const currentPlan = (authUser?.plan || "free").toLowerCase();
  const isFreePlan = currentPlan === "free";
  const isFamilyPlan = currentPlan === "family";
  const trialDaysLeft = (() => {
    if (!authUser?.trialEndsAt) return null;
    const ends = new Date(authUser.trialEndsAt).getTime();
    if (Number.isNaN(ends)) return null;
    const diff = Math.ceil((ends - Date.now()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  })();
  const isCancelled = Boolean(authUser?.cancelledAt);

  // Tab change handler — also writes to URL hash so back/forward works.
  const handleTabChange = (next) => {
    setActiveTab(next);
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#${next}`);
      window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
    }
  };

  // Listen for browser back/forward to keep activeTab in sync with hash.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onHash = () => {
      const h = window.location.hash.replace("#", "");
      if (["home", "spending", "budget", "upload"].includes(h)) {
        setActiveTab(h);
      }
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // E1+E2: drives the post-quote Razorpay open + celebration trigger
  const launchRazorpay = async (plan) => {
    setPayingPlan(plan);
    try {
      await openRazorpayCheckout(
        { plan, cycle: "monthly" },
        {
          onSuccess: (verify) => {
            // Trigger celebration before refreshing auth so the modal mounts
            // immediately with the correct plan info.
            setCelebration({
              plan: verify.plan,
              isUpgrade: verify.isUpgrade,
              fromPlan: verify.fromPlan,
            });
            refreshAuth?.();
          },
        }
      );
    } catch (e) {
      if (
        e?.message !== "dismissed" &&
        e?.code !== "already_on_plan" &&
        e?.code !== "downgrade_not_supported"
      ) {
        console.error("razorpay err", e);
      }
    } finally {
      setPayingPlan(null);
    }
  };

  const startUpgrade = async (plan) => {
    if (!authUser) {
      toast.info("Sign in with Google first to start your 15-day free trial.");
      return;
    }
    // E1: paid users see the prorated quote first; free users skip straight
    // to Razorpay (no current subscription = no proration math).
    if (isFreePlan) {
      await launchRazorpay(plan);
      return;
    }
    setQuotePlan(plan);
  };

  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
      toast.success("Back online.");
    };
    const onOffline = () => {
      setIsOnline(false);
      toast.error("You're offline. Some features may not work.");
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (report) return;
      setLoading(true);
      try {
        let data = await fetchReport(reportId);
        if (cancelled) return;
        // Async-job: if the report is still being generated, poll until ready
        // — but only for a short window, to avoid hanging the UI forever on
        // a bogus reportId (TC-196 bail-out).
        if (data?.status === "processing") {
          try {
            data = await pollReportUntilReady(reportId, {
              intervalMs: 2500,
              timeoutMs: 30 * 1000,
            });
          } catch (err) {
            if (err?.status === "timeout") {
              // Poll window expired — treat as empty state so the user isn't
              // stuck on the spinner.
              if (!cancelled) {
                setReport(null);
                setInvoiceHistory([]);
              }
              return;
            }
            throw err;
          }
          if (cancelled) return;
        }
        if (data?.status === "failed") {
          toast.error(data?.error || "Report generation failed. Please retry.");
          return;
        }
        setReport(data.reportData);
        setInvoiceHistory(data.invoiceHistory || []);
        setUserId(data.userId);
        setPhone(data.phone || "");
        setBudget(data.reportData?.budgetStatus?.budget || 9000);
        setBudgetInput(String(data.reportData?.budgetStatus?.budget || 9000));
      } catch (e) {
        console.error(e);
        // Don't surface low-level axios codes to the user — show a
        // human-readable fallback. The original message is still in the
        // browser console for debugging.
        toast.error("Could not load your report. Please refresh and try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [reportId, report]);

  const bs = report?.budgetStatus;
  const progressClass = useMemo(() => {
    if (!bs) return "";
    if (bs.pctUsed >= 100) return "danger";
    if (bs.pctUsed >= 80) return "warn";
    return "";
  }, [bs]);

  const weeklyTotal = useMemo(() => {
    if (!report?.weeklyList) return 0;
    return report.weeklyList.reduce((s, w) => s + (w.estimatedPrice || 0), 0);
  }, [report]);

  const daysSinceLastUpload = useMemo(() => {
    const latest = invoiceHistory?.[0]?.upload_date;
    if (!latest) return null;
    const diff = Math.floor((Date.now() - new Date(latest).getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  }, [invoiceHistory]);

  const onSaveBudget = async () => {
    const b = parseInt(budgetInput, 10);
    if (!Number.isFinite(b) || b < 500) {
      toast.error("Please enter a budget of at least ₹500.");
      return;
    }
    if (b > 1_000_000) {
      toast.error("Please enter a realistic monthly budget (max ₹10,00,000).");
      return;
    }
    setBudget(b);
    setEditingBudget(false);
    if (userId) {
      try {
        await updateBudget({ userId, budget: b });
        // Refresh local report budget status
        setReport((r) => {
          if (!r) return r;
          const spent = r.budgetStatus?.spent || 0;
          return {
            ...r,
            budgetStatus: {
              ...r.budgetStatus,
              budget: b,
              remaining: Math.max(b - spent, 0),
              pctUsed: b ? Math.min(Math.round((spent / b) * 100), 200) : 0,
            },
          };
        });
        toast.success("Budget updated.");
      } catch (e) {
        toast.error("Could not save budget.");
      }
    } else {
      // Anonymous — just update locally
      setReport((r) => {
        if (!r) return r;
        const spent = r.budgetStatus?.spent || 0;
        return {
          ...r,
          budgetStatus: {
            ...r.budgetStatus,
            budget: b,
            remaining: Math.max(b - spent, 0),
            pctUsed: b ? Math.min(Math.round((spent / b) * 100), 200) : 0,
          },
        };
      });
      toast.success("Budget updated locally. Add a phone to save across sessions.");
    }
  };

  const onConnectWhatsapp = async () => {
    // D6: WhatsApp alerts are part of the paid tiers. If the user is still on
    // Free, route them to the pricing section to compare plans first.
    if (isFreePlan) {
      toast.info("WhatsApp alerts are part of Starter, Pro and Family. Pick your plan.");
      document.getElementById("pricing-anchor")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    const p = waPhone.trim();
    if (!p || p.length < 8) {
      toast.error("Enter a valid WhatsApp number.");
      return;
    }
    try {
      const res = await connectWhatsapp({ phone: p, userId: userId || null });
      setUserId(res.userId);
      setWaConnected(true);
      toast.success("WhatsApp connected. Saturday reminders are on.");
    } catch (e) {
      toast.error("Could not connect WhatsApp.");
    }
  };

  const onConfirmList = async () => {
    if (!report?.weeklyList?.length) return;
    try {
      await confirmOrder({
        userId: userId || null,
        reportId,
        items: report.weeklyList,
        totalAmount: weeklyTotal,
      });
      toast.success("Weekly list saved as an order.");
    } catch (e) {
      toast.error("Could not save the list.");
    }
  };

  const downloadWeeklyPdf = () => {
    const list = report?.weeklyList || [];
    if (!list.length) {
      toast.info("No weekly list to download yet.");
      return;
    }
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const today = new Date().toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    // Header bar
    doc.setFillColor(255, 107, 0); // #FF6B00
    doc.rect(0, 0, 595, 90, "F");
    doc.setTextColor(10, 10, 15);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("CartIQ 360", 40, 45);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text("Your smart weekly grocery list", 40, 65);
    doc.setFontSize(9);
    doc.text(`Generated ${today}`, 440, 45);

    // Body
    doc.setTextColor(20, 20, 25);
    let y = 130;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("#", 40, y);
    doc.text("ITEM", 70, y);
    doc.text("QTY", 290, y);
    doc.text("CATEGORY", 360, y);
    doc.text("PRICE (₹)", 510, y, { align: "right" });
    doc.setDrawColor(220, 220, 220);
    doc.line(40, y + 6, 555, y + 6);
    y += 22;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    list.forEach((w, i) => {
      if (y > 780) {
        doc.addPage();
        y = 60;
      }
      const tag = w.essential ? "●" : "○";
      doc.setFont("helvetica", "bold");
      doc.text(String(i + 1), 40, y);
      doc.setFont("helvetica", "normal");
      doc.text(`${tag} ${(w.item || "").slice(0, 40)}`, 70, y);
      doc.text(String(w.qty || "1"), 290, y);
      doc.text(String(w.category || "—"), 360, y);
      doc.text(String(w.estimatedPrice ?? 0), 510, y, { align: "right" });
      y += 18;
    });

    // Footer totals
    y += 10;
    doc.setDrawColor(10, 10, 15);
    doc.line(40, y, 555, y);
    y += 22;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Estimated total", 40, y);
    doc.text(`₹ ${weeklyTotal.toLocaleString("en-IN")}`, 510, y, { align: "right" });

    const bs = report?.budgetStatus || {};
    if (bs.budget) {
      y += 18;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(
        `Monthly budget ₹${(bs.budget || 0).toLocaleString("en-IN")} · Remaining ₹${(bs.remaining || 0).toLocaleString("en-IN")}`,
        40,
        y
      );
    }
    y += 28;
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text("● Essential  ○ Optional", 40, y);
    doc.text("cartiq360.app · Solutionist", 555, y, { align: "right" });

    doc.save(`cartiq360-weekly-list-${today.replace(/\s+/g, "-")}.pdf`);
    toast.success("List downloaded.");
  };

  const OfflineBanner = () =>
    !isOnline ? (
      <div
        className="sticky top-0 z-50 bg-[#E24B4A] text-white text-center py-2 text-xs uppercase tracking-[0.2em] font-bold"
        data-testid="offline-banner"
      >
        You're offline · showing cached data · changes will sync when you reconnect
      </div>
    ) : null;

  if (loading) {
    return (
      <div className="cartiq-wellness min-h-screen bg-[#F8FAFC] text-[#334155] flex flex-col" data-testid="dashboard-loading">
        <OfflineBanner />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-10 h-10 mx-auto border-2 border-[#FF6B00] border-t-transparent rounded-full animate-spin" />
            <div className="text-sm uppercase tracking-wider text-[#888]">Loading your intelligence</div>
          </div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div
        className="cartiq-wellness min-h-screen bg-[#F8FAFC] text-[#334155] flex flex-col"
        data-testid="dashboard-empty-state"
      >
        <OfflineBanner />
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-md w-full text-center space-y-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold">
              — No invoices yet
            </div>
            <h1 className="font-display uppercase text-4xl leading-tight">
              Upload your first invoice to get started
            </h1>
            <p className="text-sm text-[#888] leading-relaxed">
              Drop a Blinkit, Zepto, Swiggy or Zomato PDF and we'll unpack your spend,
              spot your patterns, and send smart weekly grocery lists to your WhatsApp.
            </p>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="btn-saffron"
              data-testid="empty-upload-btn"
            >
              Upload your first invoice →
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { summary, categoryBreakdown, platformBreakdown, topItems, insights, weeklyList, feesSummary } = report;

  // D2/D4/D5: derive reliable counts from the upload ledger so the dashboard
  // never shows "0 invoices / 0 platforms" while invoices are clearly listed.
  // `invoiceHistory` is sourced from /api/pdf_uploads (one row per upload),
  // independent of Claude's per-invoice item extraction success.
  const totalInvoiceCount = Math.max(
    Number(summary?.ordersAnalysed) || 0,
    invoiceHistory?.length || 0
  );
  const platformsFromHistory = Array.from(
    new Set(
      (invoiceHistory || [])
        .map((r) => (r.platform || "").trim())
        .filter((p) => p && p !== "Other")
    )
  );
  const totalPlatformCount = Math.max(
    platformBreakdown?.length || 0,
    platformsFromHistory.length
  );

  // Header element passed to AppShell — same logical content as before, just
  // sized for the new tab layout (no max-w-7xl since the shell handles width).
  const headerEl = (
    <header className="border-b border-[#2A2A35] bg-[#0A0A0F]/80 backdrop-blur sticky top-0 z-30">
      <div className="px-4 lg:px-6 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="text-[#888] hover:text-[#F0EDE8] lg:hidden"
            aria-label="Back to landing"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="lg:hidden">
            <BrandMark size="sm" sub={false} />
          </div>
        </div>
        <div className="flex items-center gap-2 lg:gap-3">
          <GoogleAuthButton compact />
          {authUser && (
            <PlanBadge
              plan={currentPlan}
              trialDaysLeft={trialDaysLeft}
              cancelled={isCancelled}
              compact
            />
          )}
          {isFreePlan && (
            <button
              type="button"
              className="btn-saffron text-xs px-3 py-1.5"
              onClick={() => startUpgrade("pro")}
              disabled={payingPlan === "pro"}
              data-testid="header-upgrade-btn"
            >
              {payingPlan === "pro" ? "Opening…" : "Upgrade"}
            </button>
          )}
        </div>
      </div>
    </header>
  );

  return (
    <div className="cartiq-wellness">
      <Toaster position="top-center" richColors theme="light" />
      <OfflineBanner />
      {shareOpen && <ShareableCard report={report} onClose={() => setShareOpen(false)} />}
      {/* E1: Prorated upgrade preview */}
      <UpgradeQuoteModal
        open={Boolean(quotePlan)}
        plan={quotePlan || "pro"}
        cycle="monthly"
        onClose={() => setQuotePlan(null)}
        onConfirm={async () => {
          const target = quotePlan;
          setQuotePlan(null);
          if (target) await launchRazorpay(target);
        }}
      />
      {/* E2: Celebration on subscription success */}
      <SubscriptionCelebrationModal
        open={Boolean(celebration)}
        plan={celebration?.plan}
        isUpgrade={celebration?.isUpgrade}
        fromPlan={celebration?.fromPlan}
        onClose={() => setCelebration(null)}
      />
      {/* Weekly list bottom sheet — opened by Home FAB */}
      <WeeklyListSheet
        open={weeklySheetOpen}
        onClose={() => setWeeklySheetOpen(false)}
        weeklyList={{
          title: "Your Saturday cart",
          items: (weeklyList || []).map((w) => ({
            item: w.item,
            category: w.category,
            quantity: w.qty,
            estimatedPrice: w.estimatedPrice,
          })),
        }}
        onConfirm={onConfirmList}
        onEdit={() => handleTabChange("spending")}
        onDownload={downloadWeeklyPdf}
      />

      <AppShell active={activeTab} onChange={handleTabChange} header={headerEl}>
        {/* Early-adopter welcome + celebration (free users only) */}
        {authUser && <EarlyAdopterPrompt />}
        <div data-testid="tab-pane" className="px-4 lg:px-8 py-6 max-w-7xl mx-auto space-y-6 lg:space-y-8 min-w-0 overflow-x-hidden w-full">

      {/* === HOME TAB === */}
      {activeTab === "home" && (
        <>
          {/* My Dashboard hero — two stat circles, month-view calendar with
              brand-coloured dots per ordered day, and a day-detail panel
              that opens when a populated day is tapped. */}
          <MyDashboardOverview />

          {/* Shared Space — SpaceCard always shows; aggregated stats hero,
              calendar, + conditional BillSplit/BudgetProgress panel only
              render when the user belongs to a space. */}
          <SharedSpaceView />

          {/* — Total spend analysis — the legacy intelligence summary the
              user explicitly wanted preserved BELOW the new dashboard. */}
          <div className="stagger-in" data-testid="home-greeting">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-1.5">
              — Total spend analysis
            </div>
            <h1
              className="font-display uppercase text-3xl md:text-5xl leading-[1.05]"
              style={{ fontFamily: "'Pirata One', serif" }}
            >
              Here's how you really eat.
            </h1>
            <p className="text-xs md:text-sm text-[#888] mt-2">
              Based on {totalInvoiceCount} {totalInvoiceCount === 1 ? "invoice" : "invoices"} across {totalPlatformCount} {totalPlatformCount === 1 ? "platform" : "platforms"}.
            </p>
          </div>

          <UploadWarningsBanner warnings={report.warnings} />

          {typeof daysSinceLastUpload === "number" && daysSinceLastUpload >= 7 && (
            <UploadReminderNudge
              daysSince={daysSinceLastUpload}
              totalInvoices={invoiceHistory?.length || 0}
            />
          )}

          {/* Summary cards */}
          <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6" data-testid="summary-cards">
            <SummaryCard
              label="Total spend analysed"
              value={formatINR(summary.totalSpend)}
              hint={`${summary.periodDays}-day window`}
              accent="#FF6B00"
              icon={Wallet}
              testid="summary-total"
            />
            <SummaryCard
              label="Invoices uploaded"
              value={totalInvoiceCount}
              hint={totalInvoiceCount === 1 ? "invoice processed" : "invoices processed"}
              accent="#378ADD"
              icon={ShoppingCart}
              testid="summary-orders"
            />
            <SummaryCard
              label="Top platform"
              value={summary.topPlatform}
              hint={
                platformBreakdown[0]
                  ? `${percent(platformBreakdown[0].percentage)} of spend`
                  : "—"
              }
              accent={PLATFORM_COLORS[summary.topPlatform] || "#FF6B00"}
              icon={TrendingUp}
              testid="summary-platform"
              valueClass="font-display text-2xl md:text-4xl"
            />
            <SummaryCard
              label="Budget used"
              value={isFreePlan ? "—" : `${percent(bs?.pctUsed || 0)}`}
              hint={
                isFreePlan
                  ? "Set a budget on Budget tab"
                  : `${formatINR(bs?.spent || 0)} of ${formatINR(bs?.budget || 0)}`
              }
              accent={
                bs?.isOver ? "#E24B4A" : (bs?.pctUsed || 0) > 80 ? "#BA7517" : "#1D9E75"
              }
              icon={Sparkles}
              testid="summary-budget"
            />
          </section>

          {/* Visual insight cards — Spend / Pattern / Saving.
              Built directly from structured report data so the bars and
              numbers are always grounded; no LLM prose to render. */}
          <section
            className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-6"
            data-testid="home-insights"
          >
            {/* ── SPEND ── horizontal-stacked platform bar + top items list */}
            {(() => {
              const totalSpend = summary?.totalSpend || 0;
              const platforms = (platformBreakdown || []).slice(0, 5);
              const items = (topItems || []).slice(0, 3);
              const accent = "#FF6B00";
              return (
                <div
                  className="cartiq-card relative overflow-hidden flex flex-col"
                  data-testid="insight-card-spend"
                >
                  <div className="accent-bar absolute top-0 left-0 right-0" style={{ background: accent }} />
                  <div className="flex items-start gap-3 mb-4">
                    <div
                      className="w-9 h-9 flex items-center justify-center shrink-0"
                      style={{ background: `${accent}15`, border: `1px solid ${accent}40` }}
                    >
                      <Wallet className="w-4 h-4" style={{ color: accent }} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1" style={{ color: accent }}>
                        Spend
                      </div>
                      <div className="font-mono-num text-2xl md:text-3xl text-[#F0EDE8] leading-none">
                        {formatINR(totalSpend)}
                      </div>
                      <div className="text-[11px] uppercase tracking-[0.18em] text-[#888] mt-1">
                        {totalInvoiceCount} {totalInvoiceCount === 1 ? "order" : "orders"} analysed
                      </div>
                    </div>
                  </div>
                  {/* Stacked platform bar */}
                  {platforms.length > 0 && totalSpend > 0 && (
                    <>
                      <div
                        className="flex h-2.5 w-full overflow-hidden mb-2 bg-[#2A2A35]"
                        data-testid="insight-spend-platform-bar"
                        aria-label="Spend split by platform"
                      >
                        {platforms.map((p, i) => {
                          const pct = Math.max(2, ((p.amount || 0) / totalSpend) * 100);
                          return (
                            <div
                              key={i}
                              style={{
                                width: `${pct}%`,
                                background: PLATFORM_COLORS[p.platform] || accent,
                              }}
                              title={`${p.platform}: ${formatINR(p.amount)}`}
                            />
                          );
                        })}
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1.5 mb-3">
                        {platforms.slice(0, 3).map((p, i) => (
                          <div key={i} className="flex items-center gap-1.5 text-[11px]">
                            <span
                              className="w-2 h-2 rounded-full"
                              style={{ background: PLATFORM_COLORS[p.platform] || accent }}
                            />
                            <span className="text-[#F0EDE8] font-bold uppercase tracking-wider">
                              {p.platform}
                            </span>
                            <span className="text-[#888] font-mono-num">
                              {percent((p.amount || 0) / totalSpend)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {/* Top items mini-rows */}
                  {items.length > 0 && (
                    <div className="mt-auto pt-2 border-t border-[#2A2A35] space-y-1.5">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#888] mb-1.5">
                        Biggest line items
                      </div>
                      {items.map((t, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between text-xs"
                          data-testid={`insight-spend-top-item-${i}`}
                        >
                          <span className="text-[#F0EDE8] truncate min-w-0 flex-1 mr-2">
                            {t.name || t.item}
                          </span>
                          <span className="font-mono-num text-[#FF6B00] shrink-0">
                            {formatINR(t.totalSpent || t.total || 0)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── PATTERN ── top-categories horizontal bars */}
            {(() => {
              const cats = (categoryBreakdown || []).slice(0, 4);
              const totalCat = cats.reduce((s, c) => s + (c.amount || 0), 0) || 1;
              const accent = "#378ADD";
              return (
                <div
                  className="cartiq-card relative overflow-hidden flex flex-col"
                  data-testid="insight-card-pattern"
                >
                  <div className="accent-bar absolute top-0 left-0 right-0" style={{ background: accent }} />
                  <div className="flex items-start gap-3 mb-4">
                    <div
                      className="w-9 h-9 flex items-center justify-center shrink-0"
                      style={{ background: `${accent}15`, border: `1px solid ${accent}40` }}
                    >
                      <TrendingUp className="w-4 h-4" style={{ color: accent }} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1" style={{ color: accent }}>
                        Pattern
                      </div>
                      <div className="text-sm md:text-base text-[#F0EDE8] font-bold uppercase tracking-tight">
                        {cats[0]?.category || "No data"} leads
                      </div>
                      <div className="text-[11px] uppercase tracking-[0.18em] text-[#888] mt-1">
                        {cats.length} categories tracked
                      </div>
                    </div>
                  </div>
                  {/* Horizontal bars */}
                  <div className="space-y-2.5">
                    {cats.map((c, i) => {
                      const pct = ((c.amount || 0) / totalCat) * 100;
                      return (
                        <div key={i} data-testid={`insight-pattern-row-${i}`}>
                          <div className="flex items-center justify-between text-[11px] mb-1">
                            <span className="text-[#F0EDE8] font-bold uppercase tracking-wider truncate pr-2">
                              {c.category}
                            </span>
                            <span className="font-mono-num text-[#888] shrink-0">
                              {formatShortINR(c.amount || 0)} · {percent((c.amount || 0) / totalCat)}
                            </span>
                          </div>
                          <div className="h-1.5 bg-[#2A2A35] overflow-hidden">
                            <div
                              className="h-full transition-all"
                              style={{
                                width: `${Math.max(2, pct)}%`,
                                background: c.color || accent,
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* ── SAVING ── fees ring + monthly savings opportunity */}
            {(() => {
              const totalSpend = summary?.totalSpend || 0;
              const fees = feesSummary?.total || 0;
              const feePct = totalSpend > 0 ? (fees / totalSpend) * 100 : 0;
              // Heuristic: batching halves handling fees → potential ₹ savings
              const potentialMonthly = Math.round(fees / 2);
              const potentialYearly = potentialMonthly * 12;
              const accent = "#1D9E75";

              // SVG ring math (radius=28, circumference=176)
              const ringR = 28;
              const ringC = 2 * Math.PI * ringR;
              const ringDash = (Math.min(feePct, 100) / 100) * ringC;

              return (
                <div
                  className="cartiq-card relative overflow-hidden flex flex-col"
                  data-testid="insight-card-saving"
                >
                  <div className="accent-bar absolute top-0 left-0 right-0" style={{ background: accent }} />
                  <div className="flex items-start gap-3 mb-3">
                    <div
                      className="w-9 h-9 flex items-center justify-center shrink-0"
                      style={{ background: `${accent}15`, border: `1px solid ${accent}40` }}
                    >
                      <Sparkles className="w-4 h-4" style={{ color: accent }} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1" style={{ color: accent }}>
                        Saving
                      </div>
                      <div className="text-sm md:text-base text-[#F0EDE8] font-bold uppercase tracking-tight">
                        Cut fees in half
                      </div>
                      <div className="text-[11px] uppercase tracking-[0.18em] text-[#888] mt-1">
                        Batch your orders
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Fee % ring */}
                    <div className="relative shrink-0" data-testid="insight-saving-ring">
                      <svg width="76" height="76" viewBox="0 0 76 76">
                        <circle cx="38" cy="38" r={ringR} fill="none" stroke="#2A2A35" strokeWidth="6" />
                        <circle
                          cx="38"
                          cy="38"
                          r={ringR}
                          fill="none"
                          stroke={accent}
                          strokeWidth="6"
                          strokeDasharray={`${ringDash} ${ringC}`}
                          strokeLinecap="round"
                          transform="rotate(-90 38 38)"
                          style={{ transition: "stroke-dasharray 600ms ease" }}
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="font-mono-num text-base md:text-lg text-[#F0EDE8] leading-none">
                          {feePct.toFixed(0)}%
                        </div>
                        <div className="text-[8px] uppercase tracking-[0.18em] text-[#888] mt-0.5">
                          fees
                        </div>
                      </div>
                    </div>
                    {/* Savings figure */}
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#888]">
                        You paid
                      </div>
                      <div className="font-mono-num text-base text-[#F0EDE8]">
                        {formatINR(fees)} <span className="text-[#888] text-xs">in fees</span>
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#888] mt-2">
                        Could save
                      </div>
                      <div className="font-mono-num text-base text-[#1D9E75]">
                        {formatINR(potentialMonthly)}<span className="text-[#888] text-xs">/mo</span>
                      </div>
                      {potentialYearly > 0 && (
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#666] mt-1">
                          ≈ {formatINR(potentialYearly)} a year
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </section>

          {/* WhatsApp automation — Coming Soon */}
          <section
            id="whatsapp-cta"
            className="bg-[#14141A] border border-[#2A2A35] p-5 md:p-6 relative overflow-hidden"
            data-testid="whatsapp-coming-soon"
          >
            <div className="grid md:grid-cols-[1fr_auto] gap-4 items-center">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#1D9E75] font-bold mb-1 flex items-center gap-2">
                  — WhatsApp automation
                  <span className="text-[9px] tracking-[0.18em] px-1.5 py-0.5 bg-[#1D9E75]/15 border border-[#1D9E75]/40 text-[#1D9E75]">
                    Coming soon
                  </span>
                </div>
                <h2 className="font-display uppercase text-2xl md:text-3xl mb-1 text-[#F0EDE8]">
                  Saturday lists in your DMs.
                </h2>
                <p className="text-xs md:text-sm text-[#888]">
                  Weekly smart lists, budget alerts, and restock reminders — landing in your WhatsApp soon.
                </p>
              </div>
              <button
                type="button"
                disabled
                className="opacity-60 cursor-not-allowed border border-[#2A2A35] text-[#666] px-4 py-2 text-xs uppercase tracking-[0.18em] font-bold flex items-center justify-center gap-2"
                data-testid="whatsapp-coming-soon-btn"
              >
                <MessageCircle className="w-4 h-4" /> Coming soon
              </button>
            </div>
          </section>

          {/* Share CTA */}
          <section data-testid="share-cta-section">
            <div className="cartiq-card relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: "linear-gradient(90deg,#E24B4A 0%,#FF6B00 50%,#1D9E75 100%)" }} />
              <div className="grid md:grid-cols-[1fr_auto] gap-4 items-center">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-1">
                    — Your food year
                  </div>
                  <h2 className="font-display uppercase text-2xl md:text-3xl mb-1">
                    Make your friends curious — <span style={{ color: "#FF6B00" }}>share this app</span>.
                  </h2>
                  <p className="text-xs md:text-sm text-[#888]">
                    Download a single image of your food year — top category, top platform, savings unlocked.
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-saffron flex items-center justify-center gap-2 w-full md:w-auto"
                  onClick={() => setShareOpen(true)}
                  data-testid="share-cta-btn"
                >
                  <Share2 className="w-4 h-4" /> Share this app
                </button>
              </div>
            </div>
          </section>
        </>
      )}

      {/* === SPENDING TAB === */}
      {activeTab === "spending" && (
        <>
          <div data-testid="spending-greeting">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-1.5">
              — Where it went
            </div>
            <h1
              className="font-display uppercase text-3xl md:text-5xl leading-[1.05]"
              style={{ fontFamily: "'Pirata One', serif" }}
            >
              Your spending breakdown.
            </h1>
          </div>
        </>
      )}

      {/* === BUDGET TAB === */}
      {activeTab === "budget" && (
        <>
          <div data-testid="budget-greeting">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-1.5">
              — Plan & guardrails
            </div>
            <h1
              className="font-display uppercase text-3xl md:text-5xl leading-[1.05]"
              style={{ fontFamily: "'Pirata One', serif" }}
            >
              Budget &amp; subscription.
            </h1>
          </div>
        </>
      )}

      {/* === UPLOAD TAB === */}
      {activeTab === "upload" && (
        <>
          <div data-testid="upload-greeting">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-1.5">
              — Add invoices
            </div>
            <h1
              className="font-display uppercase text-3xl md:text-5xl leading-[1.05]"
              style={{ fontFamily: "'Pirata One', serif" }}
            >
              Drop more receipts.
            </h1>
            <p className="text-xs md:text-sm text-[#888] mt-2">
              PDFs and screenshots from any quick-commerce or food-delivery app.
            </p>
          </div>
        </>
      )}
      {/* === Below this point: existing rendered sections, gated by tab === */}

      {/* Upload-tab content: ledger + warnings + reminder + upload zone */}
      {activeTab === "upload" && (
        <>
          {/* Quick counter strip — reuses meta from /api/auth/me so the user
              instantly sees how many invoices they've already processed
              vs their plan ceiling (#8 — feedback fix). */}
          {(() => {
            const cap = isFreePlan ? 5 : null; // free tier cap
            const processed = (invoiceHistory || []).filter(
              (i) => (i.status || "processed") === "processed"
            ).length;
            const total = invoiceHistory?.length || 0;
            return (
              <div
                className="bg-[#14141A] border border-[#2A2A35] px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
                data-testid="upload-counter-strip"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#888] font-bold">
                    Invoices on file
                  </div>
                  <div className="font-mono-num text-2xl text-[#F0EDE8]">
                    {processed}
                    {cap ? <span className="text-[#666] text-base">/{cap}</span> : null}
                  </div>
                  {total > processed && (
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#BA7517] font-bold">
                      · {total - processed} set aside
                    </span>
                  )}
                </div>
                {cap && (
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#888]">
                    {Math.max(0, cap - processed)} of {cap} free uploads left
                  </div>
                )}
              </div>
            );
          })()}

          {/* Drop zone — primary action of this tab. We route to the landing
              page's upload flow because that's where the batched-compress +
              processing-overlay machinery lives. Keeps Dashboard.jsx lean. */}
          <button
            type="button"
            onClick={() => navigate("/upload")}
            className="cartiq-card hover:border-[#FF6B00] transition-colors text-left w-full"
            data-testid="upload-tab-cta"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#FF6B00]/10 border border-[#FF6B00]/40 shrink-0">
                <Plus className="w-5 h-5 text-[#FF6B00]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-0.5">
                  — Drop invoices or screenshots
                </div>
                <div className="font-display uppercase text-xl md:text-2xl">
                  Add more receipts
                </div>
                <p className="text-xs text-[#888] mt-1">
                  PDFs, JPEGs, PNGs from any quick-commerce or food-delivery app — auto-batched and compressed.
                </p>
              </div>
              <ArrowUpRight className="w-5 h-5 text-[#FF6B00] shrink-0" />
            </div>
          </button>

          {/* A3: Upload ledger */}
          <InvoiceHistoryTable
            invoices={invoiceHistory}
            currentPlan={currentPlan}
            timeline={report?.timeline || []}
            familyMembers={authUser?.familyMembers || []}
            onUpgrade={() => {
              if (isFreePlan) {
                handleTabChange("budget");
              } else {
                setQuotePlan("pro");
              }
            }}
          />

          <UploadWarningsBanner warnings={report.warnings} />

          {typeof daysSinceLastUpload === "number" && daysSinceLastUpload >= 7 && (
            <UploadReminderNudge
              daysSince={daysSinceLastUpload}
              totalInvoices={invoiceHistory?.length || 0}
            />
          )}
        </>
      )}

      {/* Spending-tab content gate opens here and closes before the budget grid */}
      {activeTab === "spending" && (
        <>

        {/* SECTION 1: Category breakdown */}
        <section data-testid="spend-section-category">
          <div
            className="cartiq-card overflow-hidden"
            data-testid="category-chart-card"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-1">
                  — Section 01 · Where it went
                </div>
                <h2 className="font-display uppercase text-2xl">Category breakdown</h2>
              </div>
              <div className="text-xs text-[#888] uppercase tracking-wider">
                {categoryBreakdown.length} categories
              </div>
            </div>

            <div className="grid sm:grid-cols-[220px_1fr] md:grid-cols-[260px_1fr] gap-4 sm:gap-6 items-center max-w-full">
              <div
                className="h-56 sm:h-64 mx-auto w-full max-w-[260px] overflow-hidden"
                data-testid="category-chart-canvas"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryBreakdown}
                      dataKey="amount"
                      nameKey="category"
                      innerRadius={50}
                      outerRadius={88}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {categoryBreakdown.map((c, i) => (
                        <Cell key={i} fill={c.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<DashboardTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2.5 min-w-0">
                {categoryBreakdown.slice(0, 7).map((c) => (
                  <div
                    key={c.category}
                    className="flex items-center justify-between text-sm gap-2"
                    data-testid={`cat-row-${c.category.toLowerCase()}`}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="w-2.5 h-2.5 shrink-0" style={{ background: c.color }} />
                      <span className="truncate text-[#F0EDE8] font-bold uppercase tracking-wider text-xs">
                        {c.category}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-[#888] font-mono-num text-xs">{percent(c.percentage)}</span>
                      <span className="text-[#F0EDE8] font-mono-num w-20 text-right">
                        {formatShortINR(c.amount)}
                      </span>
                    </div>
                  </div>
                ))}
                {categoryBreakdown.length === 0 && (
                  <div className="text-sm text-[#888]">No categories detected yet.</div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: Split by platform */}
        <section data-testid="spend-section-platform">
          <div className="cartiq-card" data-testid="platform-chart-card">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#1D9E75] font-bold mb-1">
              — Section 02 · Where you shopped
            </div>
            <h2 className="font-display uppercase text-2xl mb-3">Split by platform</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={platformBreakdown}
                  layout="vertical"
                  margin={{ top: 5, right: 16, left: 0, bottom: 5 }}
                >
                  <CartesianGrid stroke="#2A2A35" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fill: "#888", fontSize: 10, fontFamily: "JetBrains Mono" }}
                    tickFormatter={(v) => formatShortINR(v)}
                    axisLine={{ stroke: "#2A2A35" }}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="platform"
                    tick={{ fill: "#F0EDE8", fontSize: 11, fontWeight: 700 }}
                    width={90}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip cursor={{ fill: "#FF6B0015" }} content={<DashboardTooltip />} />
                  <Bar dataKey="amount" radius={0}>
                    {platformBreakdown.map((p, i) => (
                      <Cell key={i} fill={PLATFORM_COLORS[p.platform] || "#FF6B00"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        {/* SECTION 3: Spend month over month */}
        <section data-testid="spend-section-timeline">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#378ADD] font-bold mb-2">
            — Section 03 · Month over month
          </div>
          <SpendTimeline timeline={report.timeline} />
        </section>

        {/* SECTION 4: Overall pattern (Spend / Pattern / Saving cards).
            Same data-driven visuals used on Home, mounted here on the
            Spending tab too because users explicitly asked for them
            graphical, not text. */}
        <section data-testid="spend-section-pattern">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#BA7517] font-bold mb-2">
            — Section 04 · Overall pattern
          </div>
          <div
            className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-6"
            data-testid="insights-section"
          >
          {/* ── SPEND ── */}
          {(() => {
            const totalSpend = summary?.totalSpend || 0;
            const platforms = (platformBreakdown || []).slice(0, 5);
            const items = (topItems || []).slice(0, 3);
            const accent = "#FF6B00";
            return (
              <div
                className="cartiq-card relative overflow-hidden flex flex-col"
                data-testid="insight-spend"
              >
                <div className="accent-bar absolute top-0 left-0 right-0" style={{ background: accent }} />
                <div className="flex items-start gap-3 mb-4">
                  <div
                    className="w-9 h-9 flex items-center justify-center shrink-0"
                    style={{ background: `${accent}15`, border: `1px solid ${accent}40` }}
                  >
                    <Wallet className="w-4 h-4" style={{ color: accent }} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1" style={{ color: accent }}>
                      Spend
                    </div>
                    <div className="font-mono-num text-2xl md:text-3xl text-[#F0EDE8] leading-none">
                      {formatINR(totalSpend)}
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#888] mt-1">
                      {totalInvoiceCount} {totalInvoiceCount === 1 ? "order" : "orders"} analysed
                    </div>
                  </div>
                </div>
                {platforms.length > 0 && totalSpend > 0 && (
                  <>
                    <div className="flex h-2.5 w-full overflow-hidden mb-2 bg-[#2A2A35]">
                      {platforms.map((p, i) => {
                        const pct = Math.max(2, ((p.amount || 0) / totalSpend) * 100);
                        return (
                          <div
                            key={i}
                            style={{
                              width: `${pct}%`,
                              background: PLATFORM_COLORS[p.platform] || accent,
                            }}
                            title={`${p.platform}: ${formatINR(p.amount)}`}
                          />
                        );
                      })}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1.5 mb-3">
                      {platforms.slice(0, 3).map((p, i) => (
                        <div key={i} className="flex items-center gap-1.5 text-[11px]">
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{ background: PLATFORM_COLORS[p.platform] || accent }}
                          />
                          <span className="text-[#F0EDE8] font-bold uppercase tracking-wider">
                            {p.platform}
                          </span>
                          <span className="text-[#888] font-mono-num">
                            {percent((p.amount || 0) / totalSpend)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {items.length > 0 && (
                  <div className="mt-auto pt-2 border-t border-[#2A2A35] space-y-1.5">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#888] mb-1.5">
                      Biggest line items
                    </div>
                    {items.map((t, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-[#F0EDE8] truncate min-w-0 flex-1 mr-2">
                          {t.name || t.item}
                        </span>
                        <span className="font-mono-num text-[#FF6B00] shrink-0">
                          {formatINR(t.totalSpent || t.total || 0)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── PATTERN ── horizontal category bars */}
          {(() => {
            const cats = (categoryBreakdown || []).slice(0, 4);
            const totalCat = cats.reduce((s, c) => s + (c.amount || 0), 0) || 1;
            const accent = "#378ADD";
            return (
              <div
                className="cartiq-card relative overflow-hidden flex flex-col"
                data-testid="insight-pattern"
              >
                <div className="accent-bar absolute top-0 left-0 right-0" style={{ background: accent }} />
                <div className="flex items-start gap-3 mb-4">
                  <div
                    className="w-9 h-9 flex items-center justify-center shrink-0"
                    style={{ background: `${accent}15`, border: `1px solid ${accent}40` }}
                  >
                    <TrendingUp className="w-4 h-4" style={{ color: accent }} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1" style={{ color: accent }}>
                      Pattern
                    </div>
                    <div className="text-sm md:text-base text-[#F0EDE8] font-bold uppercase tracking-tight">
                      {cats[0]?.category || "No data"} leads
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#888] mt-1">
                      {cats.length} categories tracked
                    </div>
                  </div>
                </div>
                <div className="space-y-2.5">
                  {cats.map((c, i) => {
                    const pct = ((c.amount || 0) / totalCat) * 100;
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between text-[11px] mb-1">
                          <span className="text-[#F0EDE8] font-bold uppercase tracking-wider truncate pr-2">
                            {c.category}
                          </span>
                          <span className="font-mono-num text-[#888] shrink-0">
                            {formatShortINR(c.amount || 0)} · {percent((c.amount || 0) / totalCat)}
                          </span>
                        </div>
                        <div className="h-1.5 bg-[#2A2A35] overflow-hidden">
                          <div
                            className="h-full transition-all"
                            style={{
                              width: `${Math.max(2, pct)}%`,
                              background: c.color || accent,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ── SAVING ── fees ring + monthly savings opportunity */}
          {(() => {
            const totalSpend = summary?.totalSpend || 0;
            const fees = feesSummary?.total || 0;
            const feePct = totalSpend > 0 ? (fees / totalSpend) * 100 : 0;
            const potentialMonthly = Math.round(fees / 2);
            const potentialYearly = potentialMonthly * 12;
            const accent = "#1D9E75";
            const ringR = 28;
            const ringC = 2 * Math.PI * ringR;
            const ringDash = (Math.min(feePct, 100) / 100) * ringC;
            return (
              <div
                className="cartiq-card relative overflow-hidden flex flex-col"
                data-testid="insight-saving"
              >
                <div className="accent-bar absolute top-0 left-0 right-0" style={{ background: accent }} />
                <div className="flex items-start gap-3 mb-3">
                  <div
                    className="w-9 h-9 flex items-center justify-center shrink-0"
                    style={{ background: `${accent}15`, border: `1px solid ${accent}40` }}
                  >
                    <Sparkles className="w-4 h-4" style={{ color: accent }} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1" style={{ color: accent }}>
                      Saving
                    </div>
                    <div className="text-sm md:text-base text-[#F0EDE8] font-bold uppercase tracking-tight">
                      Cut fees in half
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#888] mt-1">
                      Batch your orders
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="relative shrink-0">
                    <svg width="76" height="76" viewBox="0 0 76 76">
                      <circle cx="38" cy="38" r={ringR} fill="none" stroke="#2A2A35" strokeWidth="6" />
                      <circle
                        cx="38"
                        cy="38"
                        r={ringR}
                        fill="none"
                        stroke={accent}
                        strokeWidth="6"
                        strokeDasharray={`${ringDash} ${ringC}`}
                        strokeLinecap="round"
                        transform="rotate(-90 38 38)"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div className="font-mono-num text-base md:text-lg text-[#F0EDE8] leading-none">
                        {feePct.toFixed(0)}%
                      </div>
                      <div className="text-[8px] uppercase tracking-[0.18em] text-[#888] mt-0.5">
                        fees
                      </div>
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#888]">
                      You paid
                    </div>
                    <div className="font-mono-num text-base text-[#F0EDE8]">
                      {formatINR(fees)} <span className="text-[#888] text-xs">in fees</span>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#888] mt-2">
                      Could save
                    </div>
                    <div className="font-mono-num text-base text-[#1D9E75]">
                      {formatINR(potentialMonthly)}<span className="text-[#888] text-xs">/mo</span>
                    </div>
                    {potentialYearly > 0 && (
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#666] mt-1">
                        ≈ {formatINR(potentialYearly)} a year
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
          </div>
        </section>

        {/* Top items */}
        <section className="cartiq-card" data-testid="top-items-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-1">
                — Your most-bought
              </div>
              <h2 className="font-display uppercase text-2xl">Top items</h2>
            </div>
            <div className="text-xs text-[#888] uppercase tracking-wider">
              Top {topItems?.length || 0}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full cartiq-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Category</th>
                  <th className="text-right">Times</th>
                  <th className="text-right">Total spent</th>
                  <th className="text-right">Next restock</th>
                </tr>
              </thead>
              <tbody>
                {(topItems || []).map((t, i) => {
                  const daysToRestock = (() => {
                    if (!t.nextRestockDate) return null;
                    const d = new Date(t.nextRestockDate);
                    if (isNaN(d)) return null;
                    return Math.floor((d - new Date()) / 86400000);
                  })();
                  const soon = daysToRestock !== null && daysToRestock <= 3;
                  return (
                    <tr
                      key={`${t.item}-${i}`}
                      className={
                        soon
                          ? "bg-[#FF6B00]/10 border-l-4 border-[#FF6B00]"
                          : ""
                      }
                      data-testid={`top-item-${i}`}
                    >
                      <td className="font-bold max-w-[220px] truncate" title={t.item}>
                        {t.item}
                        {soon && (
                          <span className="ml-2 text-[9px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold">
                            · Restock soon
                          </span>
                        )}
                      </td>
                      <td className="text-[#888] uppercase tracking-wider text-xs">{t.category}</td>
                      <td className="text-right font-mono-num">{t.timesOrdered}</td>
                      <td className="text-right font-mono-num text-[#FF6B00]">{formatINR(t.totalSpent)}</td>
                      <td
                        className={`text-right font-mono-num ${
                          soon ? "text-[#FF6B00] font-bold" : "text-[#1D9E75]"
                        }`}
                      >
                        {formatDate(t.nextRestockDate)}
                      </td>
                    </tr>
                  );
                })}
                {(!topItems || topItems.length === 0) && (
                  <tr>
                    <td colSpan={5} className="text-center text-[#888] text-sm py-8">
                      No items detected. Try uploading a clearer PDF invoice.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Handling charges + fees aggregate — surfaced separately so they
              don't pollute the Top Items leaderboard. Collapsible on click. */}
          {feesSummary && feesSummary.totalSpent > 0 && (
            <FeesSummaryRow data={feesSummary} totalSpend={summary?.totalSpend || 0} />
          )}
        </section>
        </>
      )}

      {/* === BUDGET TAB CONTENT === */}
      {activeTab === "budget" && (
        <>
        {/* Paid-by pie — self-hides when only "You" is paying */}
        <PaidByPieCard invoiceHistory={invoiceHistory} />

        {/* Weekly list + Budget */}
        <section className="grid lg:grid-cols-2 gap-4 md:gap-6">
          <div className="cartiq-card" data-testid="weekly-list-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#1D9E75] font-bold mb-1">
                  — Smart list
                </div>
                <h2 className="font-display uppercase text-2xl">This week's groceries</h2>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#888]">Estimated total</div>
                <div className="font-mono-num text-2xl text-[#FF6B00]">{formatINR(weeklyTotal)}</div>
              </div>
            </div>
            <ul className="divide-y divide-[#2A2A35]">
              {(weeklyList || []).map((w, i) => (
                <li
                  key={`${w.item}-${i}`}
                  className="flex items-center justify-between py-2.5"
                  data-testid={`weekly-item-${i}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className={`text-[9px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 shrink-0 ${
                        w.essential
                          ? "bg-[#FF6B00] text-[#0A0A0F]"
                          : "border border-[#555566] text-[#888]"
                      }`}
                      data-testid={`weekly-item-${i}-tag`}
                    >
                      {w.essential ? "Essential" : "Optional"}
                    </span>
                    <div className="min-w-0">
                      <div className="font-bold text-sm truncate">{w.item}</div>
                      <div className="text-[11px] uppercase tracking-wider text-[#888]">
                        {w.qty} · {w.category}
                      </div>
                    </div>
                  </div>
                  <div className="font-mono-num text-sm text-[#F0EDE8]">{formatINR(w.estimatedPrice)}</div>
                </li>
              ))}
              {(!weeklyList || weeklyList.length === 0) && (
                <li className="text-sm text-[#888] py-4">Upload more invoices for a smarter list.</li>
              )}
            </ul>
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                className="btn-saffron flex-1 flex items-center justify-center gap-2 min-w-[140px]"
                onClick={onConfirmList}
                data-testid="weekly-confirm-btn"
              >
                <CheckCircle2 className="w-4 h-4" /> Save as order
              </button>
              <button
                type="button"
                className="btn-ghost flex items-center gap-2"
                onClick={downloadWeeklyPdf}
                data-testid="weekly-download-btn"
              >
                <Download className="w-4 h-4" /> Download list
              </button>
              <button
                type="button"
                className="btn-ghost flex items-center gap-2"
                onClick={() => {
                  const text = (weeklyList || [])
                    .map((w) => `• ${w.item} (${w.qty}) — ₹${w.estimatedPrice}`)
                    .join("\n");
                  navigator.clipboard?.writeText(
                    `CartIQ 360 — Weekly List\n${text}\nTotal: ₹${weeklyTotal}`
                  );
                  toast.success("List copied to clipboard.");
                }}
                data-testid="weekly-copy-btn"
              >
                Copy
              </button>
            </div>
          </div>

          <div className="cartiq-card" data-testid="budget-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-1">
                  — Budget
                </div>
                <h2 className="font-display uppercase text-2xl" data-testid="budget-month-label">
                  {bs?.monthLabel || new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
                </h2>
              </div>
              {!isFreePlan && (
                <button
                  type="button"
                  className="text-xs text-[#888] hover:text-[#F0EDE8] uppercase tracking-wider flex items-center gap-1"
                  onClick={() => setEditingBudget((v) => !v)}
                  data-testid="budget-edit-btn"
                >
                  <Edit3 className="w-3.5 h-3.5" /> {editingBudget ? "Cancel" : "Edit"}
                </button>
              )}
            </div>

            {isFreePlan ? (
              <div
                className="p-4 border-l-[3px] border-[#FF6B00] bg-[#FF6B00]10 mb-4"
                data-testid="budget-upgrade-prompt"
              >
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-1.5">
                  Budget tracker — paid feature
                </div>
                <div className="text-sm text-[#F0EDE8] mb-3 leading-relaxed">
                  Set a monthly food budget and get live WhatsApp alerts at 60%, 80% and 100% spend.
                  Available on every paid plan — free for 15 days.
                </div>
                <button
                  type="button"
                  onClick={() => startUpgrade("starter")}
                  disabled={payingPlan === "starter"}
                  className="btn-saffron text-sm"
                  data-testid="budget-upgrade-btn"
                >
                  {payingPlan === "starter" ? "Opening…" : "Unlock budget tracking →"}
                </button>
              </div>
            ) : editingBudget ? (
              <div className="flex gap-2 mb-4" data-testid="budget-edit-form">
                <input
                  type="number"
                  value={budgetInput}
                  onChange={(e) => setBudgetInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onSaveBudget();
                    } else if (e.key === "Escape") {
                      setEditingBudget(false);
                      setBudgetInput(String(budget));
                    }
                  }}
                  onBlur={() => {
                    if (String(parseInt(budgetInput, 10)) !== String(budget)) {
                      onSaveBudget();
                    }
                  }}
                  className="cartiq-input font-mono-num flex-1"
                  min={500}
                  max={1000000}
                  step={100}
                  autoFocus
                  data-testid="budget-input"
                />
                <button type="button" className="btn-saffron" onClick={onSaveBudget} data-testid="budget-save-btn">
                  Save
                </button>
              </div>
            ) : (
              <div className="flex items-end justify-between mb-4">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#888]">Monthly</div>
                  <div className="font-mono-num text-4xl">{formatINR(budget)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#888]">Spent</div>
                  <div className={`font-mono-num text-2xl ${bs?.isOver ? "text-[#E24B4A]" : "text-[#FF6B00]"}`}>
                    {formatINR(bs?.spent || 0)}
                  </div>
                </div>
              </div>
            )}

            {!isFreePlan && (
              <>
                <div className="progress-track" data-testid="budget-progress">
                  <div
                    className={`progress-fill ${progressClass}`}
                    style={{
                      width: `${Math.min(bs?.pctUsed || 0, 100)}%`,
                      transition: "width 600ms cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-xs text-[#888] font-mono-num">
                  <span>{percent(bs?.pctUsed || 0)} used</span>
                  <span>{formatINR(bs?.remaining || 0)} remaining</span>
                </div>

                {bs?.isOver && (
                  <div
                    className="mt-3 p-3 bg-[#E24B4A]15 border-l-[3px] border-[#E24B4A] text-[13px] text-[#F0EDE8] leading-relaxed"
                    data-testid="budget-over-message"
                  >
                    <span className="font-bold text-[#E24B4A]">Over budget</span> · You've spent ₹
                    {((bs?.spent || 0) - (bs?.budget || 0)).toLocaleString("en-IN")} more than your monthly target. Review your
                    projections and cut back over the next {bs?.daysLeft ?? 0} days.
                  </div>
                )}

                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-[#2A2A35]">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#888]">Days left</div>
                    <div className="font-mono-num text-xl" data-testid="budget-days-left">{bs?.daysLeft ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#888]">Daily limit</div>
                    <div
                      className={`font-mono-num text-xl ${bs?.isOver ? "text-[#E24B4A]" : "text-[#1D9E75]"}`}
                      data-testid="budget-daily-limit"
                    >
                      {bs?.isOver ? "—" : formatINR(bs?.dailyLimit || 0)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#888]">Projected</div>
                    <div
                      className={`font-mono-num text-xl ${
                        (bs?.projectedTotal || 0) > (bs?.budget || 0) ? "text-[#E24B4A]" : "text-[#F0EDE8]"
                      }`}
                      data-testid="budget-projected"
                    >
                      {formatINR(bs?.projectedTotal || 0)}
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="mt-4 text-xs text-[#888] leading-relaxed">
              We'll nudge you in-app at 60%, 80% and 100% of your budget.
              <span className="text-[#555]"> WhatsApp alerts coming soon.</span>
            </div>
          </div>
        </section>

        {/* Subscription + Family (paid users only) — Refer & Earn and the
            primary Subscription card now live on /pricing so the Budget tab
            stays focused on the actual budget. We still keep the family
            members management here because it's a per-month admin task. */}
        {!isFreePlan && isFamilyPlan && (
          <section
            className="grid gap-4 md:gap-6 md:grid-cols-1"
            data-testid="family-section"
          >
            <FamilyMembersCard />
          </section>
        )}

        {/* Past months' budget history (paid users) */}
        {!isFreePlan && <BudgetHistoryCard />}

        {/* Compare months — visible to all authed users */}
        {authUser && <MonthCompare />}

        {/* Subscription plans removed from Budget tab — users find them on
            the dedicated /pricing route via the bottom nav. */}
        </>
      )}

      {/* Footer always visible */}
      <footer className="pt-6 border-t border-[#2A2A35] text-xs text-[#555566] uppercase tracking-[0.18em] flex flex-col md:flex-row justify-between gap-2">
        <div>CartIQ 360 · Solutionist · <a href="mailto:support@solutionist.co.in" className="hover:text-[#F0EDE8]">support@solutionist.co.in</a></div>
        <div>Generated {formatDate(new Date().toISOString())}</div>
      </footer>

        </div>

        {/* Floating Action Button — Home tab only, opens weekly list bottom sheet */}
        {activeTab === "home" && (
          <button
            type="button"
            onClick={() => setWeeklySheetOpen(true)}
            data-testid="weekly-list-fab"
            className="fixed bottom-20 right-4 lg:bottom-8 lg:right-8 z-30 flex items-center gap-2 bg-[#FF6B00] text-white px-4 py-3 lg:px-5 lg:py-3.5 font-bold uppercase tracking-[0.18em] text-xs shadow-[0_8px_24px_rgba(255,107,0,0.35)] hover:bg-[#FF8500] hover:scale-105 transition-all active:scale-95"
            aria-label="Open weekly grocery list"
          >
            <ShoppingBag className="w-4 h-4" />
            <span className="hidden sm:inline">This week's list</span>
            <span className="sm:hidden">List</span>
          </button>
        )}

        {/* CartIQ Ask — conversational AI assistant, all tabs */}
        <CartIQAsk />
      </AppShell>
    </div>
  );
}

const SummaryCard = ({ label, value, hint, accent, icon: Icon, testid, valueClass }) => (
  <div className="cartiq-card relative overflow-hidden stagger-in" data-testid={testid}>
    <div className="accent-bar absolute top-0 left-0 right-0" style={{ background: accent }} />
    <div className="flex items-start justify-between mb-3">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#888] font-bold">{label}</div>
      <div
        className="w-8 h-8 flex items-center justify-center"
        style={{ background: `${accent}15`, border: `1px solid ${accent}40` }}
      >
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
    </div>
    <div className={`${valueClass || "font-mono-num text-3xl md:text-4xl"} text-[#F0EDE8] mb-1`}>
      {value}
    </div>
    <div className="text-xs text-[#555566] uppercase tracking-wider">{hint}</div>
  </div>
);


/**
 * Aggregated handling-charges / fees row shown beneath the Top Items table.
 * These line items (platform fee, packaging, taxes, delivery fee etc.) are
 * intentionally excluded from Top Items because they appear on every invoice
 * and would otherwise dominate the most-bought leaderboard. Surfaced here as
 * a single collapsible summary so the user still sees their fee burden.
 */
const FeesSummaryRow = ({ data, totalSpend }) => {
  const [open, setOpen] = useState(false);
  if (!data || !data.totalSpent) return null;
  const pct = totalSpend ? Math.round((data.totalSpent / totalSpend) * 100) : data.percentage;
  return (
    <div
      className="mt-4 border-t border-[#2A2A35] pt-4"
      data-testid="fees-summary-row"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 text-left hover:bg-[#9D3CFF]/5 transition-colors px-2 py-2"
        data-testid="fees-summary-toggle"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-8 h-8 flex items-center justify-center shrink-0"
            style={{ background: "rgba(157,60,255,0.12)", border: "1px solid rgba(157,60,255,0.4)" }}
          >
            <Wallet className="w-4 h-4 text-[#9D3CFF]" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#9D3CFF] font-bold">
              Handling & Fees · not items
            </div>
            <div className="text-sm text-[#F0EDE8]">
              {data.lineCount} {data.lineCount === 1 ? "charge" : "charges"} totalling{" "}
              <span className="font-mono-num font-bold">{formatINR(data.totalSpent)}</span>
              <span className="text-[#888]"> · {pct}% of spend</span>
            </div>
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#888] font-bold shrink-0">
          {open ? "Hide" : "Show breakdown"}
        </div>
      </button>
      {open && (
        <div
          className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm"
          data-testid="fees-summary-breakdown"
        >
          {(data.breakdown || []).map((row, i) => (
            <div
              key={`${row.name}-${i}`}
              className="flex items-center justify-between gap-3 py-1.5 border-b border-[#2A2A35]/40 last:border-0"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className="w-1 h-1 rounded-full shrink-0"
                  style={{ background: "#9D3CFF" }}
                />
                <span className="truncate text-[#F0EDE8]">{row.name}</span>
                <span className="text-[10px] text-[#555] uppercase tracking-wider shrink-0">
                  ×{row.timesOrdered}
                </span>
              </div>
              <span className="font-mono-num text-[#F0EDE8] shrink-0">
                {formatINR(row.totalSpent)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
